import { types } from "mobx-state-tree";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Queryable } from "modules/common/models/queryable";
import { SectionCollapser } from "modules/common/models/section-collapser";
import { Transport } from "modules/common/models/transport";
import { buildCacheStorage } from "modules/common/services/cache";
import { apiUrls } from "modules/common/services/communication/urls";
import { nameof } from "modules/common/services/typescript";
import { texts } from "modules/common/texts";
import {
    OrderDictionary,
    initialState as emptyOrders,
} from "modules/orders-manage/models/order-dictionary";
import { SaveDayCommand } from "./plan-diff";
import { WorkloadPlanModes } from "modules/session/settings/models/settings";
import { WorkloadRangeResponse } from "./plan-builder";
import { PlanScale } from "./plan-scale";

const NAME = "WorkloadPlanStore";
const cache = buildCacheStorage(NAME);
const zoomKey = () => nameof((s: WorkloadPlanStoreSnapshotType) => s.zoom) as string;
const scaleKey = () => nameof((s: WorkloadPlanStoreSnapshotType) => s.scale) as string;
const dayMonthsKey = () => nameof((s: WorkloadPlanStoreSnapshotType) => s.monthsOnPageDay) as string;
const weekMonthsKey = () => nameof((s: WorkloadPlanStoreSnapshotType) => s.monthsOnPageWeek) as string;

/**
 * Глубина показа настраивается отдельно для каждого масштаба: в дневной сетке
 * месяц занимает втрое больше места, чем в недельной, и разумные значения разные.
 */
export const MONTHS_LIMITS: { [key in PlanScale]: { min: number; max: number; deflt: number } } = {
    day: { min: 2, max: 5, deflt: 3 },
    week: { min: 2, max: 8, deflt: 6 },
};

/**
 * Сколько месяцев грузим сверх видимых. Буфер не показывается, но участвует в модели:
 * когда расширение блока выталкивает соседей за правый край видимой сетки,
 * им есть куда уехать, и правка сохранится вместе с остальными.
 */
export const MONTHS_BUFFER = 2;

const clampMonths = (scale: PlanScale, value: number) => {
    const limits = MONTHS_LIMITS[scale];
    return Math.min(limits.max, Math.max(limits.min, value));
};

/**
 * Стор страницы «План работ» отвечает только за транспорт и настройки вида.
 * Сама модель плана живёт в состоянии страницы (обычные структуры + useReducer)
 */
export const WorkloadPlanStore = types
    .compose(
        Transport,
        Notificator,
        Queryable,
        types.model({
            orders: OrderDictionary,
            month: types.number,
            year: types.number,
            zoom: types.number,
            /** Масштаб сетки: колонка-день или неделя целиком */
            scale: types.enumeration<PlanScale>("PlanScale", ["day", "week"]),
            /** Сколько месяцев показывать в дневном масштабе */
            monthsOnPageDay: types.number,
            /** Сколько месяцев показывать в недельном масштабе */
            monthsOnPageWeek: types.number,
            /** Сколько месяцев реально пришло в последнем ответе сервера */
            loadedMonths: types.number,
            /** Поведение планового табеля из настроек: приезжает вместе с планом */
            planMode: types.string,
            loader: types.number,
            saver: types.number,
            collapser: SectionCollapser,
        })
    )
    .views((self) => ({
        get isLoading() {
            return self.loader > 0;
        },

        get isSaving() {
            return self.saver > 0;
        },

        get transform() {
            return +(self.zoom / 100).toFixed(2);
        },

        /** Сколько месяцев показывать при текущем масштабе */
        get monthsOnPage() {
            return self.scale === "week" ? self.monthsOnPageWeek : self.monthsOnPageDay;
        },

        /**
         * Грузим по максимуму обоих масштабов плюс буфер — тогда переключение
         * день/неделя обходится без запроса к серверу, каким бы ни был режим.
         */
        get monthsRequired() {
            return Math.max(self.monthsOnPageDay, self.monthsOnPageWeek) + MONTHS_BUFFER;
        },

        /** Данных не хватает под текущие настройки — нужен новый запрос */
        get needsReload() {
            return self.loadedMonths < Math.max(self.monthsOnPageDay, self.monthsOnPageWeek) + MONTHS_BUFFER;
        },
    }))
    .actions((self) => ({
        setPeriod(year: number, month: number) {
            self.year = year;
            self.month = month;
        },

        setZoom(value: number) {
            cache.set(zoomKey(), value);
            self.zoom = value;
        },

        setScale(value: PlanScale) {
            cache.set(scaleKey(), value);
            self.scale = value;
        },

        /** Количество месяцев запоминается отдельно для дневного и недельного масштаба */
        setMonthsOnPage(value: number) {
            const months = clampMonths(self.scale, value);

            if (self.scale === "week") {
                cache.set(weekMonthsKey(), months);
                self.monthsOnPageWeek = months;
            } else {
                cache.set(dayMonthsKey(), months);
                self.monthsOnPageDay = months;
            }
        },

        loadOrders: flow(function* () {
            if (self.orders.isEmpty) {
                yield self.orders.load();
            }
        }),

        fetch: flow(function* (year?: number, month?: number) {
            self.loader++;

            const monthsCount = self.monthsRequired;

            try {
                const data: WorkloadRangeResponse = yield self.transport.get<WorkloadRangeResponse>(
                    apiUrls.workload.range,
                    {
                        params: {
                            year: year || self.year,
                            month: month || self.month,
                            monthsCount,
                        },
                    }
                );

                self.loadedMonths = monthsCount;
                self.planMode = data.planMode || WorkloadPlanModes.timesheetDriven;

                return data;
            } catch (er) {
                self.notify.error(er);
                return null;
            } finally {
                self.loader--;
            }
        }),

        persist: flow(function* (days: SaveDayCommand[], oldDays: SaveDayCommand[]) {
            if (!days.length) {
                return true;
            }

            self.saver++;

            try {
                yield self.transport.post<any>(apiUrls.workload.batch, { days, oldDays });
                self.notify.success(texts.messages.saved);

                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            } finally {
                self.saver--;
            }
        }),

        /**
         * Свести план с табелем: невыполненное уезжает вправо, сделанное досрочно
         * снимается с будущих дней. Ночью то же самое делает фоновая задача.
         */
        reconcile: flow(function* () {
            self.saver++;

            try {
                const result: ReconcileResponse = yield self.transport.post<ReconcileResponse>(
                    apiUrls.workload.reconcile
                );

                self.notify.success(
                    result && result.changed
                        ? `План сведён с табелем: изменено дней — ${result.changed}`
                        : "План уже соответствует табелю"
                );

                // работы, вытолкнутые за конец горизонта планирования, в план не поместились
                if (result && result.overflow) {
                    self.notify.warning(
                        `У ${result.overflow} сотр. работы не поместились до конца периода планирования — ` +
                            "перенесите их вручную"
                    );
                }

                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            } finally {
                self.saver--;
            }
        }),
    }))
    .named(NAME);

interface ReconcileResponse {
    employee: number;
    days: number;
    changed: number;
    /** У скольких сотрудников работы не поместились до конца горизонта планирования */
    overflow: number;
}

export type WorkloadPlanStoreType = typeof WorkloadPlanStore.Type;
export type WorkloadPlanStoreSnapshotType = typeof WorkloadPlanStore.SnapshotType;

export const initialState = (): WorkloadPlanStoreSnapshotType => {
    const now = new Date();

    return {
        orders: emptyOrders(),
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        zoom: cache.get(zoomKey(), 100),
        scale: cache.get<PlanScale>(scaleKey(), "day"),
        monthsOnPageDay: clampMonths("day", cache.get(dayMonthsKey(), MONTHS_LIMITS.day.deflt)),
        monthsOnPageWeek: clampMonths("week", cache.get(weekMonthsKey(), MONTHS_LIMITS.week.deflt)),
        loadedMonths: 0,
        planMode: WorkloadPlanModes.timesheetDriven,
        loader: 0,
        saver: 0,
        collapser: { opened: {} },
        pureQuery: "",
        query: "",
    };
};
